import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { adminApi } from '../services/api';

type UserRow = { _id: string; username: string; isAdmin: boolean; createdAt: string | null };

// Konten aus diesem Zeitraum wurden durch einen Fehler doppelt gehasht (Login unmoeglich).
const BUG_SINCE = new Date('2025-02-21T00:00:00Z');
const BUG_FIXED = new Date('2026-10-16T00:00:00Z');

const isSuspicious = (u: UserRow) => {
  if (!u.createdAt) return false;
  const d = new Date(u.createdAt);
  return d >= BUG_SINCE && d < BUG_FIXED;
};

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [target, setTarget] = useState<UserRow | null>(null);
  const [customPassword, setCustomPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ username: string; password: string } | null>(null);
  const [dialogError, setDialogError] = useState('');

  const load = useCallback(async () => {
    try {
      setUsers(await adminApi.listUsers());
    } catch (err: any) {
      setError(err.response?.data?.error || 'Benutzer konnten nicht geladen werden');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(
    () => (users ?? []).filter((u) => u.username.toLowerCase().includes(filter.trim().toLowerCase())),
    [users, filter]
  );

  const closeDialog = () => {
    setTarget(null);
    setCustomPassword('');
    setDialogError('');
    setResult(null);
  };

  const reset = async () => {
    if (!target) return;
    setBusy(true);
    setDialogError('');
    try {
      setResult(await adminApi.resetPassword(target.username, customPassword || undefined));
    } catch (err: any) {
      setDialogError(err.response?.data?.error || 'Zurücksetzen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
        Benutzerverwaltung
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Wer sich nicht mehr einloggen kann, bekommt hier ein neues Passwort. Konten, die zwischen Feb. 2025 und
        Okt. 2026 registriert wurden, sind markiert – bei ihnen war das Passwort durch einen Fehler nie gültig.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TextField
        size="small"
        fullWidth
        label="Benutzer suchen"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        sx={{ mb: 2 }}
      />

      {!users && !error && (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      )}

      {users && (
        <TableContainer component={Paper} elevation={1}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Benutzername</TableCell>
                <TableCell>Registriert</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((u) => (
                <TableRow key={u._id} hover>
                  <TableCell>
                    {u.username}
                    {u.isAdmin && <Chip label="Admin" size="small" color="primary" sx={{ ml: 1 }} />}
                    {isSuspicious(u) && (
                      <Chip label="evtl. defekt" size="small" color="warning" variant="outlined" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>{u.createdAt ? new Date(u.createdAt).toLocaleDateString('de-DE') : '–'}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => setTarget(u)}>Passwort setzen</Button>
                  </TableCell>
                </TableRow>
              ))}
              {visible.length === 0 && (
                <TableRow><TableCell colSpan={3} align="center">Keine Benutzer gefunden</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!target} onClose={closeDialog} fullWidth maxWidth="xs">
        <DialogTitle>Passwort für „{target?.username}“</DialogTitle>
        <DialogContent>
          {result ? (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>Passwort wurde gesetzt. Bitte jetzt weitergeben – es wird nicht erneut angezeigt.</Alert>
              <TextField
                fullWidth
                label="Neues Passwort"
                value={result.password}
                InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }}
                onFocus={(e) => e.target.select()}
              />
            </>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Leer lassen für ein zufälliges Passwort, oder selbst eines vergeben (min. 6 Zeichen).
              </DialogContentText>
              {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
              <TextField
                fullWidth
                autoFocus
                label="Neues Passwort (optional)"
                value={customPassword}
                onChange={(e) => setCustomPassword(e.target.value)}
                autoComplete="off"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {result ? (
            <>
              <Button onClick={() => navigator.clipboard?.writeText(result.password)}>Kopieren</Button>
              <Button variant="contained" onClick={closeDialog}>Fertig</Button>
            </>
          ) : (
            <>
              <Button onClick={closeDialog}>Abbrechen</Button>
              <Button variant="contained" onClick={reset} disabled={busy}>Passwort setzen</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminUsers;
